import { useCallback, useRef } from "react";
import type { PointerEvent } from "react";

type DragState = {
  pointerId: number;
  lastClientX: number;
  lastClientY: number;
};

function canStartDrag(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return !target.closest(
    "button,a,input,textarea,select,option,[role='button'],[role='link'],[contenteditable='true'],[data-no-drag]",
  );
}

/** Pointer-captured dragging that reports each move as a delta. Controls
 * inside the element never start a drag. While dragging, the element carries
 * `data-dragging="true"`. Every pointer-down is kept from the layers below. */
export function usePointerDragOffset(
  enabled: boolean,
  onDelta: (dx: number, dy: number) => void,
) {
  const dragRef = useRef<DragState | null>(null);
  const onDeltaRef = useRef(onDelta);
  onDeltaRef.current = onDelta;

  const onPointerDown = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      event.stopPropagation();
      if (!enabled || event.button !== 0 || !canStartDrag(event.target)) {
        return;
      }
      dragRef.current = {
        pointerId: event.pointerId,
        lastClientX: event.clientX,
        lastClientY: event.clientY,
      };
      event.currentTarget.dataset.dragging = "true";
      event.currentTarget.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    },
    [enabled],
  );
  const onPointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.stopPropagation();
    const dx = event.clientX - drag.lastClientX;
    const dy = event.clientY - drag.lastClientY;
    drag.lastClientX = event.clientX;
    drag.lastClientY = event.clientY;
    onDeltaRef.current(dx, dy);
    event.preventDefault();
  }, []);
  const onPointerEnd = useCallback((event: PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    delete event.currentTarget.dataset.dragging;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    event.stopPropagation();
  }, []);
  /** Ends an in-flight drag on `element`, releasing its pointer capture. */
  const releaseDrag = useCallback((element: HTMLElement | null) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!element) return;
    delete element.dataset.dragging;
    if (drag && element.hasPointerCapture?.(drag.pointerId)) {
      element.releasePointerCapture(drag.pointerId);
    }
  }, []);

  return {
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: onPointerEnd,
    },
    releaseDrag,
  };
}
